import { Observable, fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Injectable, Renderer2, RendererFactory2, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'; // Assuming ng-bootstrap for modal

@Injectable({
  providedIn: 'root',
})
export class WindowService {
  public openWindows: Record<string, string> = {};
  private closeCallbacks: Record<string, () => void> = {};
  private renderer: Renderer2;
  public openModalRefs: Record<string, any> = {};
  constructor(
    private modalService: NgbModal,
    private rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }
  windowIsOpen(id: any): boolean {
    return id in this.openWindows;
  }

  openMapEntityDetailsWindow(
    mapEntityWindowId: string,
    compiledContent: HTMLElement,
    autoSize = true,
    closeCallback?: () => void
  ): void {
    if (!(mapEntityWindowId in this.openWindows)) {
      const mainCanvas = this.document.getElementById('mainCanvas');
      if (mainCanvas) {
        this.renderer.appendChild(mainCanvas, compiledContent);
        this.openWindows[mapEntityWindowId] =
          this.getMapEntityDetailsWindowId(mapEntityWindowId);

        if (closeCallback) {
          this.closeCallbacks[mapEntityWindowId] = closeCallback;
        }

        if (autoSize) {
          const startingHeight = compiledContent.offsetHeight;
          const windowContents =
            compiledContent.querySelector('#windowContents');
          if (windowContents) {
            // Listen for changes in content height
            const observer = new MutationObserver(() => {
              const contentsHeight = windowContents.clientHeight;
              this.renderer.setStyle(
                compiledContent,
                'height',
                `${startingHeight + contentsHeight}px`
              );
            });
            observer.observe(windowContents, {
              subtree: true,
              childList: true,
              characterData: true,
            });
          }
        }
      }
    }
  }

  dockWindow(windowId: string, percentage?: number, type = 'dockleft'): void {
    if (windowId in this.openWindows) {
      const elem = this.document.getElementById(this.openWindows[windowId]);
      if (!elem) return;

      this.renderer.addClass(elem, type);
      this.renderer.setStyle(
        elem,
        'height',
        `${this.getHeightOfAvailableWindowSpace()}px`
      );
      const top = `${this.getSessionBarHeight()}px`;
      const bottom = `${this.getTimelineHeight()}px`;
      this.renderer.setStyle(elem, 'top', top);
      this.renderer.setStyle(elem, 'bottom', bottom);

      if (percentage && percentage > 0) {
        const widthPx = this.calculatePercentage(percentage);
        if (widthPx > 0) {
          this.renderer.setStyle(elem, 'width', `${widthPx}px`);
        }
      }
    }
  }

  fillMapEntityWindowContents(mapEntityWindowId: string): void {
    if (mapEntityWindowId in this.openWindows) {
      const windowElem = this.document.getElementById(
        this.openWindows[mapEntityWindowId]
      );
      const header = this.document
        .getElementById(`header-${mapEntityWindowId}`)
        ?.querySelector('.detailsHeader');
      const footer = this.document
        .getElementById(`footer-${mapEntityWindowId}`)
        ?.querySelector('.detailsFooter');
      const windowContents = windowElem?.querySelector('#windowContents');

      if (windowElem && header && footer && windowContents) {
        const totalHeight = windowElem.clientHeight;
        const headerHeight = header.clientHeight;
        const footerHeight = footer.clientHeight;
        this.renderer.setStyle(
          windowContents,
          'height',
          `${totalHeight - headerHeight - footerHeight}px`
        );
      }
    }
  }

  getHeightOfWindowSpace(mapEntityWindowId: string): number | undefined {
    if (mapEntityWindowId in this.openWindows) {
      const windowElem = this.document.getElementById(
        this.openWindows[mapEntityWindowId]
      );
      const header = this.document
        .getElementById(`header-${mapEntityWindowId}`)
        ?.querySelector('.detailsHeader');
      const footer = this.document
        .getElementById(`footer-${mapEntityWindowId}`)
        ?.querySelector('.detailsFooter');

      if (windowElem && header && footer) {
        const totalHeight = windowElem.clientHeight;
        const headerHeight = header.clientHeight;
        const footerHeight = footer.clientHeight;
        return totalHeight - headerHeight - footerHeight;
      }
    }
    return undefined;
  }

  openNonMapEntityWindow(windowId: string, compiledContent: HTMLElement): void {
    if (!(windowId in this.openWindows)) {
      const mainCanvas = this.document.getElementById('mainCanvas');
      if (mainCanvas) {
        this.renderer.appendChild(mainCanvas, compiledContent);
        this.openWindows[windowId] = windowId;
      }
    }
  }

  isEntityDetailsWindowOpen(mapEntityWindowId: string): boolean {
    return mapEntityWindowId in this.openWindows;
  }

  closeMapEntityDetailsWindow(mapEntityWindowId: any): void {
    if (typeof mapEntityWindowId === 'object') {
      this.removeAllCameraWindows();
      mapEntityWindowId = mapEntityWindowId.getWindowId();
    }

    if (this.openModalRefs[mapEntityWindowId]) {
      try {
        this.openModalRefs[mapEntityWindowId].close();
      } catch (e) {}
      delete this.openModalRefs[mapEntityWindowId];
    }
    if (mapEntityWindowId in this.openWindows) {
      const elem = this.document.getElementById(this.openWindows[mapEntityWindowId]);
      if (elem) {
        this.renderer.removeChild(elem.parentNode, elem);
      }
      delete this.openWindows[mapEntityWindowId];
    }

    if (mapEntityWindowId in this.closeCallbacks) {
      this.closeCallbacks[mapEntityWindowId]();
      delete this.closeCallbacks[mapEntityWindowId];
    }
  }

  closeAllMapEntityDetailsWindow(): void {
    Object.keys(this.openWindows).forEach((mapEntityWindowId) => {

      if (this.openModalRefs[mapEntityWindowId]) {
        try {
          this.openModalRefs[mapEntityWindowId].close();
        } catch (e) {}
        delete this.openModalRefs[mapEntityWindowId];
      }
      const elem = this.document.getElementById(this.openWindows[mapEntityWindowId]);
      if (elem) {
        this.renderer.removeChild(elem.parentNode, elem);
      }
      delete this.openWindows[mapEntityWindowId];
    });

    Object.keys(this.closeCallbacks).forEach((key) => {
      if (this.closeCallbacks[key]) {
        this.closeCallbacks[key]();
      }
      delete this.closeCallbacks[key];
    });
  }

  closeNonMapEntityWindow(windowId: string): void {
    const isRegularCamera = windowId.startsWith('camera-window');
    const isAdHocCamera = windowId.startsWith('adhoc-camera-window');
    let fmvId: string | undefined;

    if (isAdHocCamera) {
      const number = windowId.replace('adhoc-camera-window', '');
      fmvId = `fmv-attribute-visualizer-${number}`;
    } else if (isRegularCamera) {
      const sensorId = windowId.replace('camera-window', '');
      fmvId = `fmv-attribute-visualizer-${sensorId}`;
    }

    if (fmvId) {
      const fmvElems = this.document.getElementsByName(fmvId);
      Array.from(fmvElems).forEach((elem) => {
        elem.parentNode?.removeChild(elem);
      });
    }

    if (windowId in this.openWindows) {
      const elem = this.document.getElementById(this.openWindows[windowId]);
      if (elem) {
        this.renderer.removeChild(elem.parentNode, elem);
      }
      delete this.openWindows[windowId];
    }
  }

  getMapEntityDetailsWindowId(mapEntityWindowId: string): string {
    return 'details-' + mapEntityWindowId;
  }

  openModalWindow(
    data: any,
    controller: any,
    view: string,
    newWidth?: string,
    marginTop?: string,
    showInEditMode?: boolean,
    inputs?: Record<string, any>,
    modalOptions?: { windowClass?: string }
  ): Promise<any> {
    const modalParent = showInEditMode
      ? this.document.querySelector('#main-canvas-modals-always')
      : this.document.querySelector('#main-canvas-modals');

    const windowClass = modalOptions?.windowClass || 'custom-modal-window';
    const modalRef = this.modalService.open(controller, {
      backdrop: true,
      animation: true,
      container: modalParent as HTMLElement,
      // size: 'lg',
      scrollable: true,
      centered: true,
      windowClass,
    });
    if (inputs && modalRef.componentInstance) {
      Object.entries(inputs).forEach(([key, value]) => {
        modalRef.componentInstance[key] = value;
      });
    }
    modalRef.shown.subscribe(() => {
      const backdrop = this.document.querySelector('div.modal-backdrop');
      if (backdrop) {
        this.renderer.setStyle(
          backdrop,
          'backgroundColor',
          'rgba(0, 0, 0, 0.3)'
        );
      }

      const dialog = this.document.querySelector(
        '.modal-dialog'
      ) as HTMLElement;
      if (dialog) {
        dialog.style.marginTop = marginTop ?? '10%';
        dialog.style.width = newWidth ?? '700px';
      }
    });

    return modalRef.result;
  }

  encloseInWindow(
    windowId: string,
    title: string,
    contents: string,
    positioning = 'top-right-window-positioning',
    windowShape = 'defaultWindowShape'
  ): string {
    const windowDimension = 350;
    const leafletWindowWidth =
      this.document.querySelector('.leafletWindowShape')?.clientWidth ?? 410;
    const numOpenedCamera = this.getNumberOfOpenCamera();
    let top = (numOpenedCamera + 1) % 2 ? 50 : windowDimension + 50;
    let left = Math.floor(numOpenedCamera / 2) * 400 + leafletWindowWidth;
    if (numOpenedCamera >= 6) {
      top = 50;
      left = leafletWindowWidth + 30 * (numOpenedCamera - 5);
    }

    const positionStyle = `position: absolute; top: ${top}px; left: ${left}px; z-index: 999`;

    const draggable =
      title === 'Location-Based Messaging' ? '' : 'ng-draggable';

    return `
      <div id="${windowId}" ${draggable} default-window="${windowDimension}" ng-draggable-resize="true" ng-draggable-min-height="${windowDimension}" ng-draggable-min-width="${windowDimension}" class="${windowShape}" style="${positionStyle}">
        <default-window-decorator windowId="${windowId}" title="${title}"></default-window-decorator>
        ${contents}
      </div>`;
  }

  getTimelineHeight(): number {
    const elem = this.document.getElementById('arcLiveTimeline');
    return elem ? elem.clientHeight : 0;
  }

  getOpenWindows(): Record<string, string> {
    return this.openWindows;
  }

  removeAllCameraWindows(): void {
    Object.entries(this.openWindows).forEach(([key, windowId]) => {
      if (/^camera-window/.test(windowId)) {
        const elem = this.document.getElementById(windowId);
        if (elem) {
          this.renderer.removeChild(elem.parentNode, elem);
        }
        delete this.openWindows[key];
      }
    });
  }

  removeAllPhoneBox(): void {
    const elems = this.document.querySelectorAll('phonebox');
    elems.forEach((elem) => {
      elem.parentNode?.removeChild(elem);
    });
  }

  private getHeightOfAvailableWindowSpace(): number {
    return (
      window.innerHeight - this.getSessionBarHeight() - this.getTimelineHeight()
    );
  }

  private getSessionBarHeight(): number {
    const elem = this.document.getElementById('arclive-session-options-bar');
    return elem ? elem.clientHeight : 0;
  }

  private calculatePercentage(percentage: number): number {
    if (!percentage || percentage <= 0) {
      return 0;
    }
    return window.innerWidth * percentage;
  }

  private getNumberOfOpenCamera(): number {
    return Object.values(this.openWindows).filter((id) =>
      /^camera-window/.test(id)
    ).length;
  }

  getInnerHeight(): number {
    return window.innerHeight;
  }

  onResize(): Observable<Event> {
    return fromEvent(window, 'resize').pipe(
      debounceTime(100) // Debounce resize events to prevent too many updates
    );
  }
}
